// src/dataStore.js
// Tiny client-side DB with localStorage persistence and auto IDs.
// Tables: farmers, plots, crops, biochar_applications, mrv_samples

const KEY = "agroconnect-db-v1";

function load() {
  try { return JSON.parse(localStorage.getItem(KEY) || "{}"); }
  catch { return {}; }
}
function save(db) {
  localStorage.setItem(KEY, JSON.stringify(db));
}
function nowISO() { return new Date().toISOString().slice(0, 19); }
function ensure(db, table) { db[table] = Array.isArray(db[table]) ? db[table] : []; return db[table]; }
function uuid(prefix) { return `${prefix}_${Math.random().toString(36).slice(2,10)}_${Date.now().toString(36)}`; }

const db = load();

/* -------------------- Generic helpers -------------------- */
function list(table) {
  return [...ensure(db, table)];
}
function count(table) {
  return ensure(db, table).length;
}
function upsert(table, keyField, record) {
  const tbl = ensure(db, table);
  let rec = { ...record };
  if (!rec[keyField]) rec[keyField] = uuid(keyField);
  const idx = tbl.findIndex(r => r[keyField] === rec[keyField]);
  if (idx >= 0) {
    tbl[idx] = { ...tbl[idx], ...rec };
  } else {
    tbl.push({ ...rec, created_at: rec.created_at || nowISO() });
  }
  save(db);
  return rec;
}
function setVerified(table, keyField, id, byEmail) {
  const tbl = ensure(db, table);
  const i = tbl.findIndex(r => r[keyField] === id);
  if (i >= 0) {
    tbl[i].verified = true;
    tbl[i].verified_by = byEmail;
    tbl[i].verified_at = nowISO();
    save(db);
    return true;
  }
  return false;
}

/* -------------------- Domain helpers -------------------- */
// Farmers
function upsertFarmer(f) {
  return upsert("farmers", "farmer_id", {
    farmer_id: f.farmer_id,
    full_name: f.full_name || "",
    email: f.email || "",
    country: f.country || "",
    region: f.region || "",
    district: f.district || "",
    village: f.village || "",
    gps_lat: f.gps_lat ?? "",
    gps_lng: f.gps_lng ?? "",
    verified: !!f.verified,
    aggregator_id: f.aggregator_id || "",
    created_at: f.created_at,
  });
}
function verifyFarmer(id, by) { return setVerified("farmers","farmer_id", id, by); }

// Plots
function upsertPlot(p) {
  return upsert("plots", "plot_id", {
    plot_id: p.plot_id,
    farmer_id: p.farmer_id,
    plot_name: p.plot_name || "",
    gps_lat: p.gps_lat ?? "",
    gps_lng: p.gps_lng ?? "",
    area_ha: p.area_ha != null ? Number(p.area_ha) : undefined,
    verified: !!p.verified,
    created_at: p.created_at,
  });
}
function verifyPlot(id, by) { return setVerified("plots","plot_id", id, by); }

// Crops
function upsertCrop(c) {
  return upsert("crops", "crop_entry_id", {
    crop_entry_id: c.crop_entry_id,
    plot_id: c.plot_id,
    season_year: c.season_year,
    season_name: c.season_name || "",
    crop_species: c.crop_species || "",
    variety: c.variety || "",
    planting_date: c.planting_date || "",
    harvest_date: c.harvest_date || "",
    yield_kg: c.yield_kg != null ? Number(c.yield_kg) : undefined,
    market_channel: c.market_channel || "",
    price_local_per_kg: c.price_local_per_kg != null ? Number(c.price_local_per_kg) : undefined,
    verified: !!c.verified,
    created_at: c.created_at,
  });
}
function verifyCrop(id, by) { return setVerified("crops","crop_entry_id", id, by); }

// Biochar
function upsertBiochar(b) {
  return upsert("biochar_applications","application_id",{
    application_id: b.application_id,
    plot_id: b.plot_id,
    date_applied: b.date_applied || "",
    feedstock_type: b.feedstock_type || "",
    kiln_type: b.kiln_type || "",
    biochar_mass_kg: b.biochar_mass_kg != null ? Number(b.biochar_mass_kg) : undefined,
    application_rate_t_per_ha: b.application_rate_t_per_ha != null ? Number(b.application_rate_t_per_ha) : undefined,
    incorporation_method: b.incorporation_method || "",
    co_composted: !!b.co_composted,
    estimated_fixed_C_fraction: b.estimated_fixed_C_fraction != null ? Number(b.estimated_fixed_C_fraction) : undefined,
    notes: b.notes || "",
    verified: !!b.verified,
    created_at: b.created_at,
  });
}
function verifyBiochar(id, by) { return setVerified("biochar_applications","application_id", id, by); }

// Soil samples (MRV)
function upsertSample(s) {
  return upsert("mrv_samples","sample_id",{
    sample_id: s.sample_id,
    plot_id: s.plot_id,
    sample_date: s.sample_date || "",
    sample_depth_cm: s.sample_depth_cm != null ? Number(s.sample_depth_cm) : undefined,
    soil_organic_carbon_pct: s.soil_organic_carbon_pct != null ? Number(s.soil_organic_carbon_pct) : undefined,
    bulk_density_g_cm3: s.bulk_density_g_cm3 != null ? Number(s.bulk_density_g_cm3) : undefined,
    ph: s.ph != null ? Number(s.ph) : undefined,
    moisture_pct: s.moisture_pct != null ? Number(s.moisture_pct) : undefined,
    lab_id: s.lab_id || "",
    method_ref: s.method_ref || "",
    verified: !!s.verified,
    created_at: s.created_at,
  });
}
function verifySample(id, by) { return setVerified("mrv_samples","sample_id", id, by); }

/* -------------------- Bulk CSV helper for TSP -------------------- */
function bulkInsertAuto(table, rows) {
  let inserted = 0, updated = 0;
  rows.forEach((r) => {
    const keyField =
      table === "farmers" ? "farmer_id" :
      table === "plots" ? "plot_id" :
      table === "crops" ? "crop_entry_id" :
      table === "biochar_applications" ? "application_id" :
      table === "mrv_samples" ? "sample_id" : "id";
    const before = ensure(db, table).length;
    upsert(table, keyField, r);
    const after = ensure(db, table).length;
    if (after > before) inserted++; else updated++;
  });
  return { inserted, updated };
}

/* -------------------- Exports -------------------- */
export default {
  list, count,
  upsertFarmer, verifyFarmer,
  upsertPlot, verifyPlot,
  upsertCrop, verifyCrop,
  upsertBiochar, verifyBiochar,
  upsertSample, verifySample,
  bulkInsertAuto,
};